import { format, isValid, parse } from 'date-fns';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import {
  deleteObject,
  getBytes,
  ref,
  uploadBytes,
} from 'firebase/storage';
import {
  createZipBlob,
  extractZipFiles,
  triggerFileDownload,
  triggerZipDownload,
} from './csvExport';
import {
  createBackupFilename,
  createInvoiceDownloadFilename,
} from './backupFilename';
import {
  createBackupMetadataFile,
  validateBackupMetadata,
} from './backupMetadata';

const MAX_BACKUP_SIZE = 100 * 1024 * 1024;
const MAX_PDF_SIZE = 20 * 1024 * 1024;
const MAX_INVOICE_COUNT = 5000;

const sanitizeFilenamePart = (value, fallback) => {
  const withoutControlCharacters = Array.from(String(value || ''))
    .filter((character) => character.charCodeAt(0) >= 32)
    .join('');
  const cleaned = withoutControlCharacters
    .trim()
    .replace(/[<>:"/\\|?*]/g, '-')
    .replace(/\s+/g, ' ')
    .replace(/[. ]+$/g, '');
  return cleaned || fallback;
};

const getInvoiceDate = (dateValue, dateFormat) => {
  if (!dateValue) return null;
  if (typeof dateValue?.toDate === 'function') {
    return dateValue.toDate();
  }
  if (dateValue instanceof Date) {
    return dateValue;
  }
  if (typeof dateValue === 'string' && dateFormat) {
    const parsed = parse(dateValue, dateFormat, new Date());
    if (isValid(parsed)) return parsed;
  }
  const parsed = new Date(dateValue);
  return isValid(parsed) ? parsed : null;
};

export const createInvoicePdfFilename = (invoice, dateFormat = 'dd-MM-yyyy') => {
  const orderNumber = sanitizeFilenamePart(invoice?.orderNumber, 'Unknown');
  const customerName = sanitizeFilenamePart(invoice?.customerName, 'Unknown-Customer')
    .replace(/\s+/g, '_');
  const invoiceDate = getInvoiceDate(invoice?.invoiceDate, dateFormat);
  const datePart = invoiceDate ? format(invoiceDate, 'yyyy-MM-dd') : 'Unknown-Date';

  return `Order-${orderNumber}-${customerName}-${datePart}.pdf`;
};

const normalizeManifestValue = (value) => {
  if (value === null || value === undefined) return value ?? null;
  if (typeof value?.toDate === 'function') {
    return value.toDate().toISOString();
  }
  if (value instanceof Date) {
    return value.toISOString();
  }
  if (Array.isArray(value)) {
    return value.map(normalizeManifestValue);
  }
  if (typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value).map(([key, nestedValue]) => [
        key,
        normalizeManifestValue(nestedValue),
      ])
    );
  }
  return value;
};

const collectInvoiceFiles = async ({
  invoices,
  storage,
}) => {
  const downloadableInvoices = invoices.filter(
    (invoice) => invoice.id && invoice.storagePath
  );
  const results = await Promise.allSettled(
    downloadableInvoices.map(async (invoice) => {
      const storageRef = ref(storage, invoice.storagePath);
      const bytes = await getBytes(storageRef);
      const invoiceId = sanitizeFilenamePart(invoice.id, 'invoice');
      const archivePath = `invoices/${invoiceId}.pdf`;

      return {
        file: {
          filename: archivePath,
          data: new Uint8Array(bytes),
        },
        invoice,
        manifestEntry: {
          id: invoice.id,
          archivePath,
          storagePath: invoice.storagePath,
          data: normalizeManifestValue(
            Object.fromEntries(
              Object.entries(invoice).filter(([key]) => key !== 'id')
            )
          ),
        },
      };
    })
  );

  const successfulResults = results
    .filter((result) => result.status === 'fulfilled')
    .map((result) => result.value);

  return {
    successfulResults,
    skippedCount: invoices.length - successfulResults.length,
  };
};

export const downloadInvoiceBackup = async ({
  invoices,
  activeFarmId,
  farmName,
  storage,
}) => {
  const { successfulResults, skippedCount } = await collectInvoiceFiles({
    invoices,
    storage,
  });

  if (successfulResults.length === 0) {
    return { downloadedCount: 0, skippedCount };
  }

  const manifest = {
    format: 'micgro-invoice-backup',
    version: 1,
    exportedAt: new Date().toISOString(),
    farmId: activeFarmId || null,
    invoiceCount: successfulResults.length,
    invoices: successfulResults.map((result) => result.manifestEntry),
  };
  const encoder = new TextEncoder();
  const files = [
    createBackupMetadataFile(),
    ...successfulResults.map((result) => result.file),
    {
      filename: 'invoice-manifest.json',
      data: encoder.encode(JSON.stringify(manifest, null, 2)),
    },
  ];
  const zipBlob = createZipBlob(files);
  const dateStamp = format(new Date(), 'yyyy-MM-dd');
  triggerZipDownload(createBackupFilename(farmName, 'Invoice', dateStamp), zipBlob);

  return {
    downloadedCount: successfulResults.length,
    skippedCount,
  };
};

export const downloadInvoicePdfsZip = async ({
  invoices,
  farmName,
  dateFormat,
  storage,
}) => {
  const { successfulResults, skippedCount } = await collectInvoiceFiles({
    invoices,
    storage,
  });

  if (successfulResults.length === 0) {
    return { downloadedCount: 0, skippedCount };
  }

  const usedFilenames = new Set();
  const files = successfulResults.map((result) => {
    const requestedFilename = createInvoicePdfFilename(result.invoice, dateFormat);
    const baseName = requestedFilename.replace(/\.pdf$/i, '');
    let filename = requestedFilename;
    let duplicateNumber = 2;

    while (usedFilenames.has(filename.toLowerCase())) {
      filename = `${baseName}-${duplicateNumber}.pdf`;
      duplicateNumber += 1;
    }
    usedFilenames.add(filename.toLowerCase());

    return {
      filename,
      data: result.file.data,
    };
  });
  const zipBlob = createZipBlob(files);
  const dateStamp = format(new Date(), 'yyyy-MM-dd');
  triggerZipDownload(createInvoiceDownloadFilename(farmName, dateStamp), zipBlob);

  return {
    downloadedCount: successfulResults.length,
    skippedCount,
  };
};

export const downloadInvoicePdf = async ({
  invoice,
  dateFormat,
  storage,
}) => {
  if (!invoice?.storagePath) {
    throw new Error('Invoice PDF is unavailable.');
  }

  const bytes = await getBytes(ref(storage, invoice.storagePath));
  const pdfBlob = new Blob([bytes], { type: 'application/pdf' });
  triggerFileDownload(
    createInvoicePdfFilename(invoice, dateFormat),
    pdfBlob
  );
};

const isPdf = (data) => (
  data.length >= 5
  && data[0] === 0x25
  && data[1] === 0x50
  && data[2] === 0x44
  && data[3] === 0x46
  && data[4] === 0x2d
);

const isSafeInvoiceId = (value) => (
  typeof value === 'string'
  && value.length > 0
  && value.length <= 1500
  && !value.includes('/')
  && value !== '.'
  && value !== '..'
);

export const importInvoiceBackup = async ({
  backupFile,
  activeFarmId,
  db,
  storage,
}) => {
  if (!backupFile || backupFile.size > MAX_BACKUP_SIZE) {
    throw new Error('Invoice backup must be 100 MB or smaller.');
  }
  if (!activeFarmId) {
    throw new Error('No active farm selected.');
  }

  const files = await extractZipFiles(backupFile);
  validateBackupMetadata(files);
  const fileMap = new Map();
  files.forEach((file) => {
    if (fileMap.has(file.filename)) {
      throw new Error('Invoice backup contains duplicate files.');
    }
    fileMap.set(file.filename, file.data);
  });
  const manifestData = fileMap.get('invoice-manifest.json');
  if (!manifestData) {
    throw new Error('Invoice manifest is missing.');
  }

  let manifest;
  try {
    manifest = JSON.parse(new TextDecoder().decode(manifestData));
  } catch {
    throw new Error('Invoice manifest is invalid.');
  }

  if (
    manifest?.format !== 'micgro-invoice-backup'
    || manifest?.version !== 1
    || !Array.isArray(manifest?.invoices)
    || manifest.invoiceCount !== manifest.invoices.length
  ) {
    throw new Error('Unsupported invoice backup format.');
  }
  if (manifest.invoices.length > MAX_INVOICE_COUNT) {
    throw new Error('Invoice backup contains too many invoices.');
  }

  const seenIds = new Set();
  const validEntries = manifest.invoices.map((entry) => {
    if (!isSafeInvoiceId(entry?.id) || seenIds.has(entry.id)) {
      throw new Error('Invoice manifest contains an invalid or duplicate ID.');
    }
    seenIds.add(entry.id);

    const expectedArchivePath = `invoices/${sanitizeFilenamePart(entry.id, 'invoice')}.pdf`;
    if (entry.archivePath !== expectedArchivePath) {
      throw new Error('Invoice manifest contains an invalid PDF path.');
    }

    const pdfData = fileMap.get(entry.archivePath);
    if (!pdfData) {
      throw new Error(`PDF is missing for invoice ${entry.id}.`);
    }
    if (pdfData.length === 0 || pdfData.length > MAX_PDF_SIZE || !isPdf(pdfData)) {
      throw new Error(`Invalid PDF for invoice ${entry.id}.`);
    }
    if (!entry.data || typeof entry.data !== 'object' || Array.isArray(entry.data)) {
      throw new Error(`Invoice data is invalid for invoice ${entry.id}.`);
    }

    return { entry, pdfData };
  });

  let importedCount = 0;
  let skippedCount = 0;
  let failedCount = 0;

  for (const { entry, pdfData } of validEntries) {
    const invoiceRef = doc(db, 'farms', activeFarmId, 'invoices', entry.id);
    let uploadedRef = null;
    try {
      const existing = await getDoc(invoiceRef);
      if (existing.exists()) {
        skippedCount += 1;
        continue;
      }

      const storagePath = `farms/${activeFarmId}/invoices/${entry.id}.pdf`;
      uploadedRef = ref(storage, storagePath);
      await uploadBytes(uploadedRef, pdfData, {
        contentType: 'application/pdf',
      });
      await setDoc(invoiceRef, {
        ...entry.data,
        storagePath,
      });
      importedCount += 1;
    } catch {
      if (uploadedRef) {
        await deleteObject(uploadedRef).catch(() => {});
      }
      failedCount += 1;
    }
  }

  return {
    importedCount,
    skippedCount,
    failedCount,
  };
};
